import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Fade from "@mui/material/Fade";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSquareFacebook,
  faYoutube,
  faInstagram,
  faSquareXTwitter,
  faSquareThreads,
  faReddit,
  faQuora,
  faPinterest,
} from "@fortawesome/free-brands-svg-icons";
import {
  faMicrochip,
  faTableList,
  faCircleExclamation,
  faFilePdf,
  faRepeat,
  faInfinity,
  faStarOfLife,
  faCheckDouble
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import api, { modelsApi } from "../../../../API/axiosInstance";
import dayjs from "dayjs";
import { MyContext } from "../../../../MyContextApi";
import { showToast } from "./../toastUtils";
import { generateHash } from "../../../../API/hashUtil";
import { handleSentimentSummary } from "../../../../API/SentimentSummary";
import processedSessionData from "../../../../API/processedData";
import CardSkeleton from "./CardSkeleton";
import "../style/SessionCard.css";

/* ─── Platform icon helper ──────────────────────────────────── */
const PLATFORM_MAP = {
  all: {
    icon: faStarOfLife,
    color: "linear-gradient(135deg, #7e5bef, #a978f9)",
  },
  facebook: { icon: faSquareFacebook, color: "#1877f2" },
  twitter: { icon: faSquareXTwitter, color: "#181919" },
  youtube: { icon: faYoutube, color: "#ff0000" },
  instagram: { icon: faInstagram, color: "#e1306c" },
  threads: { icon: faSquareThreads, color: "#6633cc" },
  reddit: { icon: faReddit, color: "#ff4500" },
  quora: { icon: faQuora, color: "#181919" },
  pinterest: { icon: faPinterest, color: "#e60023" }, // added
};

const PlatformIcon = ({ platform }) => {
  const match = PLATFORM_MAP[platform?.toLowerCase()];
  return match ? (
    <FontAwesomeIcon
      icon={match.icon}
      style={{ color: match.color, fontSize: "25px" }}
    />
  ) : (
    <FontAwesomeIcon
      icon={faCircleExclamation}
      style={{ color: "#ef4444", fontSize: "25px" }}
    />
  );
};

/* ─── Inline count badge (sits inside buttons) ──────────────── */
const CountBadge = ({ count, variant = "raw" }) => {
  const styles = {
    raw: {
      background: "rgba(139, 92, 246, 0.15)",
      color: "#5b21b6",
      border: "1px solid rgba(139, 92, 246, 0.3)",
    },
    processed: {
      background: "rgba(16, 185, 129, 0.15)",
      color: "#065f46",
      border: "1px solid rgba(16, 185, 129, 0.3)",
    },
  };
  return (
    <span
      style={{
        ...styles[variant],
        fontSize: "10px",
        fontWeight: 700,
        padding: "1px 6px",
        borderRadius: "8px",
        marginLeft: "6px",
        lineHeight: "16px",
        display: "inline-flex",
        alignItems: "center",
        minWidth: "18px",
        justifyContent: "center",
        letterSpacing: "0.02em",
      }}
    >
      {count != null ? count.toLocaleString() : "—"}
    </span>
  );
};

/* ─── SessionCard ───────────────────────────────────────────── */
export const SessionCard = ({
  session,
  selectedPlatform,
  currentPage,
  fetchSessionCards,
  crawlIsloadings,
  onRetryCrawl,
  onViewRaw, // ← new
  onViewProcessed, // ← new
  onDownloadClick, // ← add this
}) => {
  const {
    statusMap,
    dateWiseData,
    setupdateStatus,
    setOpenModal,
    setsessionData,
    setAnalysisSessionData,
    AnalysisSessionData,
    platformWiseFilterData,
    setAPISelect,
    setsentimentSummary,
  } = useContext(MyContext);

  const [disabledReportButton, setReportDisabledButtons] = useState({});
  const [reportLoading, setReportLoading] = useState({});
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [sessionStatus, setSessionStatus] = useState(session.status);

  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("Token");
  const currentPath = location.pathname;

  // console.log("Rendering SessionCard with session:", session);
  /* ── Sync status from prop ── */
  useEffect(() => {
    setSessionStatus(session.status);
  }, [session.status]);

  /* ── Pending navigation after analysis data loads ── */
  useEffect(() => {
    if (!pendingNavigation) return;
    if (AnalysisSessionData?.length > 0) {
      navigate("/previewreport", { state: { AnalysisSessionData } });
      setPendingNavigation(null);
      setReportLoading((p) => ({ ...p, [pendingNavigation]: false }));
    } else if (AnalysisSessionData?.length === 0) {
      toast.error("No data available to generate report.");
      setReportDisabledButtons((p) => ({ ...p, [pendingNavigation]: true }));
      setPendingNavigation(null);
      setReportLoading((p) => ({ ...p, [pendingNavigation]: false }));
    }
  }, [AnalysisSessionData, pendingNavigation]);

  if (![0, 1, 2, 3, 4, -2, -3].includes(session.status)) return null;

  /* ── Formatters ── */
  const formatDate = (dateString) => {
    if (!dateString || isNaN(Date.parse(dateString))) return "N/A";
    const d = new Date(dateString);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString || isNaN(Date.parse(dateString))) return "N/A";
    const d = new Date(dateString);
    let h = d.getHours();
    const min = String(d.getMinutes()).padStart(2, "0");
    const per = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${formatDate(dateString)}  ${h}:${min} ${per}`;
  };

  const formatTime = (t) => {
    if (!t) return "N/A";
    const [h, m, s] = t.split(":").map(Number);
    const totalMin = h * 60 + m;
    const sec = Math.floor(s);
    return `${totalMin > 0 ? totalMin + " min " : ""}${sec} sec`;
  };

  /* ── API helpers ── */
  const getStatus = async (sessionID) => {
    const hash = generateHash({ sessionId: String(sessionID) });
    try {
      const { data } = await api.get(`/api/sessionStatus`, {
        params: { sessionId: sessionID, HPPcode: hash },
        headers: {
          "X-XSS-Protection": "1; mode=block",
          Authorization: `Bearer ${token}`,
          "X-Content-Type-Options": "nosniff",
          "X-Frame-Options": "DENY",
          "Referrer-Policy": "strict-origin-when-cross-origin",
          "Permissions-Policy": "camera=(), microphone=(), geolocation=()",
          "Content-Type": "application/json",
        },
      });
      setSessionStatus(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSessionDetails = (id, platform) => {
    const type = session.type === "sentiment" ? "sentiment" : "section";

    showToast(`Starting analysis — Session: ${id}`, "success");

    const endpoint =
      type === "sentiment"
        ? `/session/sentiment-pipeline`
        : `/session/section-pipeline`;

    modelsApi
      .post(
        endpoint,
        { sessionId: String(id) },
        {
          headers: {
            "X-XSS-Protection": "1; mode=block",
            Authorization: `Bearer ${token}`,
            "X-Content-Type-Options": "nosniff",
            "X-Frame-Options": "DENY",
            "Referrer-Policy": "strict-origin-when-cross-origin",
            "Permissions-Policy": "camera=(), microphone=(), geolocation=()",
            "Content-Type": "application/json",
          },
        },
      )
      .then((res) => {
        if (res.status === 200) getStatus(id);
      })
      .catch((err) => {
        console.error(err);
        const detail = err?.response?.data?.detail;
        const fallback =
          type === "sentiment"
            ? "Sentiment analysis failed. Please try again."
            : "Section analysis failed. Please try again.";
        toast.error(detail || fallback);
      });

    setTimeout(() => getStatus(id), 1000);
  };

  const getSessionDetails = async (sessionID) => {
    const pool =
      platformWiseFilterData?.length > 0
        ? platformWiseFilterData
        : (dateWiseData || []).filter((i) => i.sessionId === sessionID);
    if (pool.length > 0) setupdateStatus(statusMap[pool[0].status]?.text);

    setOpenModal(true);
    const hash = generateHash({ sessionId: String(sessionID) });
    try {
      const res = await api.get(`/api/rawData`, {
        params: { sessionId: sessionID, HPPcode: hash },
        headers: {
          "X-XSS-Protection": "1; mode=block",
          Authorization: `Bearer ${token}`,
          "X-Content-Type-Options": "nosniff",
          "X-Frame-Options": "DENY",
          "Referrer-Policy": "strict-origin-when-cross-origin",
          "Permissions-Policy": "camera=(), microphone=(), geolocation=()",
          "Content-Type": "application/json",
        },
      });
      setsessionData(res.data);
    } catch (err) {
      console.error(err);
    }

    const type =
      currentPath === "/socialMediaCrawler" ? "section" : "sentiment";
    const hash2 = generateHash({ sessionId: String(sessionID), type });
    processedSessionData(sessionID, type, hash2, token, setAnalysisSessionData);
  };

  const handlePrintButtonClick = async (sessionID) => {
    setReportLoading((p) => ({ ...p, [sessionID]: true }));
    try {
      const type =
        currentPath === "/socialMediaCrawler" ? "section" : "sentiment";
      const hash = generateHash({ sessionId: String(sessionID), type });
      let result = [];
      await processedSessionData(sessionID, type, hash, token, (d) => {
        result = d;
        setAnalysisSessionData(d);
      });

      if (result?.length > 0) {
        setAPISelect(
          currentPath === "/socialMediaCrawler"
            ? { value: "api/filter", label: "Social Media Report" }
            : { value: "api/socialSentiments", label: "Sentiment Report" },
        );
        if (currentPath !== "/socialMediaCrawler") {
          handleSentimentSummary(sessionID, token)
            .then(setsentimentSummary)
            .catch(() => setsentimentSummary([]));
        }
      }

      if (!result || result.length === 0) {
        toast.error("No processed data available to generate report.");
        setReportDisabledButtons((p) => ({ ...p, [sessionID]: true }));
        return;
      }

      navigate("/previewreport", {
        state: {
          AnalysisSessionData: result,
          sessionID,
          dateCreated: dayjs(session.dateCreated || session.datetime).format(
            "DD-MM-YYYY",
          ),
        },
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate report. Please try again.");
    } finally {
      setReportLoading((p) => ({ ...p, [sessionID]: false }));
    }
  };
  /* ── Processed count: pick based on session type ── */
  const processedCount =
    session.type === "sentiment"
      ? session.sentiment_count
      : session.section_count;
  /* ── Derived state ── */

  const status = statusMap[String(sessionStatus)];
  const isActive = [0].includes(sessionStatus);
  const isFailed = [-2, -3].includes(sessionStatus);
  const canViewRaw = sessionStatus !== 0;
  const canAnalyze = sessionStatus === 1;
  const isRawDisabled = [-2, -3].includes(sessionStatus);
  const isProcessedDisabled =
    [0, -2, -3].includes(sessionStatus) ||
    (![3, 4].includes(sessionStatus) && !processedCount);
  const isReportLoad = reportLoading[session.sessionId] || false;
  const isReportDisab = disabledReportButton[session.sessionId] || false;
  const objective =
    currentPath === "/socialMediaCrawler"
      ? "Section Analysis"
      : "Sentiment Analysis";

  /* ── Platform CSS class ── */
  const platformClass = session.platform
    ? `session-card--${session.platform.toLowerCase()}`
    : "";

  /* ── Status badge style ── */
  const badgeStyle = status?.color
    ? {
        color: status.color,
        backgroundColor: `${status.color}1a`,
        border: `1px solid ${status.color}55`,
      }
    : {
        color: "#6b7280",
        backgroundColor: "#f3f4f6",
        border: "1px solid #e5e7eb",
      };

  /* ── Card class string ── */
  const cardClass = [
    "session-card",
    platformClass,
    isActive ? "session-card--active" : "",
    isFailed ? "session-card--failed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cardClass}>
      {/* Shimmer — renders once on mount, CSS animation plays once only */}
      {isActive && <span className="session-active-shimmer" />}

      {crawlIsloadings ? (
        <CardSkeleton />
      ) : (
        <>
          {/* ── Top colour bar ── */}
          <div className="session-card__top-bar" />

          {/* ── Platform + Post count pill ── */}
          <div className="session-card__platform">
            <div className="session-card__plat-pill">
              <div className="session-card__plat-icon">
                <PlatformIcon platform={session.platform} />
              </div>
              <span className="session-card__plat-name">
                {session.platform || "Unknown"}
              </span>
            </div>
            <div className="session-card__count-pill">
              <span className="session-card__count-pill-num">
                {(session.requestedCount || 0).toLocaleString()}
              </span>
              <span
                className="session-card__count-pill-lbl"
                title={`${(session.requestedCount || 0).toLocaleString()} posts requested by user`}
              >
                Req.
              </span>
            </div>
          </div>

          {/* ── Body ── */}
          <div className="session-card__body">
            {/* Session ID */}
            <div className="session-card__id">{session.sessionId || "N/A"}</div>

            {/* Keyword hero */}
            <div className="session-card__keyword">
              <div className="session-card__kw-label">
                {session.sourceType?.charAt(0).toUpperCase() +
                  session.sourceType?.slice(1) || "Keyword"}
              </div>
              <Tooltip title={session.keywords || "No keywords"} arrow>
                <div className="session-card__kw-value">
                  {session.keywords || "Keyword"}
                </div>
              </Tooltip>
            </div>

            {/* Meta grid */}
            <div className="session-card__meta">
              {/* ── Date Created — blue family ── */}
              <div className="session-card__meta-item session-card__meta-item--date">
                <div className="session-card__meta-lbl">Date Created</div>
                <div className="session-card__meta-val session-card__meta-val--date">
                  {formatDate(session.dateCreated)}
                </div>
              </div>

              {/* ── Time Taken — teal family ── */}
              <div className="session-card__meta-item session-card__meta-item--time">
                <div className="session-card__meta-lbl">Time Taken</div>
                <div
                  className={`session-card__meta-val ${session.timeTaken ? "session-card__meta-val--time" : "session-card__meta-val--muted"}`}
                >
                  {formatTime(session.timeTaken)}
                </div>
              </div>

              {/* ── Objective + Status — pink / amber ── */}
              <div className="session-card__meta-item session-card__meta-item--wide session-card__meta-item--obj-status">
                <div className="session-card__obj-status-inner">
                  {/* Objective — pink family */}
                  <div
                    className="session-card__meta-item--obj"
                    style={{ background: "none", border: "none", padding: 0 }}
                  >
                    <div className="session-card__meta-lbl">Objective</div>
                    <div
                      className={`session-card__meta-val${session.type?.toLowerCase() === "sentiment" ? " session-card__meta-val--sentiment" : ""}`}
                    >
                      {session.type.charAt(0).toUpperCase() +
                        session.type.slice(1)}{" "}
                      Analysis
                    </div>
                  </div>

                  <div className="session-card__obj-status-divider" />

                  {/* Status — amber label */}
                  <div
                    className="session-card__meta-item--status"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      textAlign: "right",
                    }}
                  >
                    <div className="session-card__meta-lbl">Status</div>
                    <span
                      className="session-card__status-badge"
                      style={badgeStyle}
                    >
                      {status?.text || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* end body */}

          {/* ── Actions ── */}
          <div className="session-card__actions">
            {/* Row 1: Raw | Processed */}
            <div className="session-card__btn-row">
              <Tooltip
                title={<span style={{ color: "#c4b5fd" }}>View Raw Data</span>}
                arrow
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 600 }}
              >
                <span style={{ flex: 1 }}>
                  <button
                    className="session-card__btn session-card__btn--raw"
                    disabled={isRawDisabled}
                    onClick={() => onViewRaw(session)}
                    style={{ width: "100%", pointerEvents: isRawDisabled ? "none" : "auto" }}
                  >
                    <FontAwesomeIcon
                      icon={faTableList}
                      style={{ fontSize: "11px" }}
                    />
                    Raw
                    <CountBadge count={session.postCount} variant="raw" />
                  </button>
                </span>
              </Tooltip>

              <Tooltip
                title={
                  <span style={{ color: "#6ee7b7" }}>View Processed Data</span>
                }
                arrow
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 600 }}
              >
                <span style={{ flex: 1 }}>
                  <button
                    className="session-card__btn session-card__btn--processed"
                    disabled={isProcessedDisabled}
                    onClick={() => onViewProcessed(session)}
                    style={{ width: "100%", pointerEvents: isProcessedDisabled ? "none" : "auto" }}
                  >
                    <FontAwesomeIcon
                      icon={faCheckDouble}
                      style={{ fontSize: "11px" }}
                    />
                    Processed
                    <CountBadge count={processedCount} variant="processed" />
                  </button>
                </span>
              </Tooltip>
            </div>

            {/* Row 2: Analyse */}
            <div className="session-card__btn-row">
              <Tooltip
                title={
                  <span style={{ color: "#6ee7b7" }}>
                    Run Analysis on this Session
                  </span>
                }
                arrow
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 600 }}
              >
                <span style={{ flex: 1 }}>
                  <button
                    className="session-card__btn session-card__btn--analyse"
                    disabled={!canAnalyze}
                    onClick={() =>
                      fetchSessionDetails(session.sessionId, session.platform)
                    }
                    style={{ width: "100%", pointerEvents: !canAnalyze ? "none" : "auto" }}
                  >
                    <FontAwesomeIcon
                      icon={faMicrochip}
                      style={{ fontSize: "11px" }}
                    />
                    Analyse
                  </button>
                </span>
              </Tooltip>
            </div>

            {/* Row 3: Retry (if failed) | Download */}
            <div className="session-card__btn-row">
              {isFailed && (
                <Tooltip
                  title={
                    <span style={{ color: "#fde68a" }}>Retry Crawling</span>
                  }
                  arrow
                  TransitionComponent={Fade}
                  TransitionProps={{ timeout: 600 }}
                >
                  <button
                    className="session-card__btn session-card__btn--retry"
                    onClick={() => onRetryCrawl(session.sessionId)}
                    style={{ flex: 1 }}
                  >
                    <FontAwesomeIcon
                      icon={faRepeat}
                      style={{ fontSize: "11px" }}
                    />
                    Retry Crawl
                  </button>
                </Tooltip>
              )}
              {!isFailed && (
                <Tooltip
                  title={
                    <span style={{ color: "#c7d2fe" }}>
                      {isReportLoad
                        ? "Generating…"
                        : isReportDisab
                          ? "Report Disabled"
                          : "Download Report"}
                    </span>
                  }
                  arrow
                  TransitionComponent={Fade}
                  TransitionProps={{ timeout: 600 }}
                >
                  <span style={{ flex: 1 }}>
                    <button
                      className="session-card__btn session-card__btn--download"
                      onClick={() => onDownloadClick(session.sessionId)}
                      style={{ width: "100%", pointerEvents: (isReportDisab || isReportLoad) ? "none" : "auto" }}
                    >
                      {isReportLoad ? (
                        <CircularProgress
                          size={12}
                          style={{ color: "#4f46e5" }}
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon={faFilePdf}
                          style={{ fontSize: "11px" }}
                        />
                      )}
                      {isReportLoad ? "Generating…" : "Download"}
                    </button>
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
          {/* end actions */}
        </>
      )}
    </div>
  );
};
export default SessionCard;
